/**
 * The token that lets a staff admin view a seller's portal.
 *
 * One place, because three read it — shopifyFetch twice and embedded.js once —
 * and three copies of "where is the token" is three chances to answer
 * differently.
 *
 * WHY IT IS LOOKED FOR IN TWO PLACES
 *
 * Impersonation used to render the seller app inside the admin page, in the
 * same tab, so the token could live in sessionStorage: same tab, same storage.
 *
 * Opening the portal in a new tab breaks that. A tab opened with `noopener` —
 * which `target="_blank"` and `window.open(..., 'noopener')` both imply — is
 * not related to its opener, and **sessionStorage is not copied into it**. The
 * new tab would load, find no token, and fail every request as though the
 * admin had never logged in.
 *
 * So the per-tab copy is tried first, and the admin's own token is the
 * fallback. That token is already in localStorage under `admin_token` —
 * AdminApp keeps it there so a refresh does not log the admin out — so
 * nothing new is being stored and no secret is being moved anywhere it was
 * not already.
 *
 * WHAT GUARDS IT
 *
 * The fallback only applies when the URL says this is an impersonation
 * (`admin_impersonate=1`). Without that, a merchant's own browser would never
 * have an `admin_token` to find — but relying on absence is not a check, and
 * the URL parameter is the thing that actually says what this page is for.
 */
import { readStored, readSession, writeSession } from './safeStorage'

/** The key AdminApp keeps the staff token under, across tabs. */
const ADMIN_TOKEN_KEY = 'admin_token'
/** The per-tab copy, set when impersonation starts in that tab. */
const IMPERSONATE_KEY = 'admin_impersonate_token'
/**
 * WHICH shop is being viewed, per tab.
 *
 * The token says an impersonation is open; it does not say whose. The shop
 * arrived only as a url parameter, and the app's first navigation strips it,
 * so after that the shop was resolved from the merchant-flow cache in
 * localStorage — which is shared by every tab on this origin.
 *
 * That is wrong in two ways, and the second is the serious one:
 *
 *   • refreshed with nothing cached, the app had no shop at all and drew the
 *     "No Shopify store connected" screen, under a banner naming the shop;
 *   • with something cached, it used whatever shop was looked at LAST. Open
 *     seller A, open seller B in a second tab, come back to A and refresh,
 *     and A's tab resolves to B — the ADMIN VIEW banner still says A while
 *     every figure on the page is B's.
 *
 * sessionStorage is per tab, so two open portals cannot overwrite each
 * other's answer, which localStorage cannot promise.
 */
const IMPERSONATE_SHOP_KEY = 'admin_impersonate_shop'

/**
 * Captured at module evaluation, before anything renders or navigates.
 *
 * This has to happen here and not inside the resolver below, because the
 * resolver is not reliably called while the parameter still exists.
 * isEmbeddedContext answers `true` at its own `admin_impersonate=1` check and
 * returns — it never reaches the token line — so a write placed in the
 * resolver ran only once the parameter was already gone, at which point the
 * resolver bails before reaching it. Unreachable in exactly the case it was
 * written for.
 *
 * `admin_impersonate=1` is on the URL only while the tab is opening; the
 * app's first navigation strips the query. A tab opened with noopener
 * inherits no sessionStorage, so unless the per-tab copy is written now there
 * is nothing left to recognise the session by: the bearer token stops being
 * sent and the app draws the "open from your Shopify admin" gate.
 *
 * Same approach as utils/shopifyHost, which remembers `host` at import time
 * for the same reason and says so.
 */
;(function captureAdminImpersonation() {
  try {
    const params = new URLSearchParams(window.location.search)
    if (params.get('admin_impersonate') !== '1') return

    if (!readSession(IMPERSONATE_KEY)) {
      const token = readStored(ADMIN_TOKEN_KEY)
      if (token) writeSession(IMPERSONATE_KEY, token)
    }
    // The shop is captured from the same load, for the same reason: it is on
    // the url now and gone after the first navigation. Written every time
    // rather than only when absent, so reusing a tab to view a different
    // seller replaces the answer instead of keeping the old one.
    const shop = params.get('shop')
    if (shop) writeSession(IMPERSONATE_SHOP_KEY, shop)
  } catch { /* no window, or storage blocked — the parameter still covers this load */ }
})()

/**
 * Is this an admin view, including after the query has been stripped?
 *
 * `isAdminImpersonation` below reads the url alone, which is correct for the
 * server-facing decisions that happen on the first load and wrong for
 * anything rendered afterwards. The Dashboard nav entry was filtered on it
 * and so vanished the moment you navigated: the parameter had gone, while the
 * session was still very much an admin one.
 */
export function isAdminView() {
  return isAdminImpersonation() || !!adminImpersonationToken()
}

/**
 * The shop this tab is impersonating, or null.
 *
 * Deliberately NOT falling back to anything. A wrong answer here shows one
 * seller's catalogue, inventory and orders while claiming to be another's, so
 * "no shop" is the only safe way to be unsure.
 */
export function adminImpersonationShop() {
  if (!isAdminView()) return null
  return readSession(IMPERSONATE_SHOP_KEY) || null
}

/** Is this page an admin viewing a seller's portal? */
export function isAdminImpersonation() {
  try {
    return new URLSearchParams(window.location.search).get('admin_impersonate') === '1'
  } catch {
    return false
  }
}

/**
 * The bearer token to use, or null when this is an ordinary merchant session.
 *
 * Both reads go through safeStorage: session and local storage each throw
 * rather than return null when site data is blocked, and this runs on the
 * first request the page makes.
 */
export function adminImpersonationToken() {
  const perTab = readSession(IMPERSONATE_KEY)
  if (perTab) return perTab
  if (!isAdminImpersonation()) return null

  const token = readStored(ADMIN_TOKEN_KEY) || null
  if (!token) return null

  // Remembered for this tab, on first use.
  //
  // Without this the session lasts exactly one page load. `admin_impersonate=1`
  // is only in the URL when the tab opens: the app's own first navigation
  // strips the query, and from then on this function found no per-tab copy —
  // a tab opened with noopener inherits none — and no parameter either, so it
  // answered null. The app decided it was not an impersonation, stopped
  // sending the bearer token, and drew the "open from your Shopify admin"
  // gate. Which is exactly what a staff member saw: the portal opened, and
  // moving anywhere in it asked them to log in to Shopify.
  //
  // The same-tab version never hit this, because the admin panel wrote the
  // per-tab copy itself before rendering. This is that write, moved to the
  // place that can still do it — the portal's own first load.
  //
  // A refused write is not fatal: the parameter is still on the URL for this
  // load, so the page works and only navigation would lose it.
  writeSession(IMPERSONATE_KEY, token)
  return token
}

export { ADMIN_TOKEN_KEY, IMPERSONATE_KEY, IMPERSONATE_SHOP_KEY }
